import { useCallback, useSyncExternalStore } from 'react';

import { getSetting, setSetting, subscribe } from '@/services/userSettingsStore';
import { FrontendSettings, FrontendSettingsKey } from '@/types/userSettings';

/**
 * Reads and writes one server-backed Dugout preference.
 *
 * Mirrors `useLocalStorage`'s ergonomics so migrating a call site is a small
 * change. The read is synchronous (served from the localStorage cache), so it
 * is safe to use for values needed during the first render.
 */
const useUserSetting = <K extends FrontendSettingsKey>(
  key: K,
  defaultValue: NonNullable<FrontendSettings[K]>
): [NonNullable<FrontendSettings[K]>, (value: FrontendSettings[K]) => void] => {
  const value = useSyncExternalStore(
    subscribe,
    () => getSetting(key),
    () => getSetting(key)
  );

  const setValue = useCallback((next: FrontendSettings[K]) => setSetting(key, next), [key]);

  return [value ?? defaultValue, setValue];
};

export default useUserSetting;
